/* eslint-disable react-refresh/only-export-components */
// Exports the pure `identitySignature` / `buildIdentityPushes` helpers next to
// the tracker so they are unit-testable (same rationale as
// `product-detail-tracker.tsx`); Fast Refresh is irrelevant to a leaf
// analytics side-effect component.
import { useEffect, useRef } from "react";

import { pushDataLayer } from "~/hec/analytics/data-layer.client";

/** The signed-in identity keys GTM reads off the dataLayer (SFCC-11009/-11024). */
export type DataLayerIdentity = {
  isAuthenticated: boolean;
  userId: string | null;
  hashedEmail: string | null;
  heapId: string | null;
  customerLoyalty: boolean;
  customerGroups: string[];
};

/** Stable signature so the tracker re-fires only when the identity changes. */
export const identitySignature = (id: DataLayerIdentity): string =>
  JSON.stringify([
    id.isAuthenticated,
    id.userId ?? "",
    id.hashedEmail ?? "",
    id.heapId ?? "",
    id.customerLoyalty,
    id.customerGroups,
  ]);

/**
 * The dataLayer objects for an identity transition — the same five keys the
 * hard-load push in `DataLayerClientPushes` writes (which mirrors SFRA
 * `dataLayerHelper.initializeGTMDataLayer`).
 *
 * Deliberately NOT the same emission rules as that first-paint push, which
 * skips `heapId` when absent, gates `userId`/`hashedEmail`/`customerLoyalty`
 * on `isAuthenticated`, and skips `customerGroupSalesforce` on an empty list
 * (`gtm-inline.tsx:419-427`). A transition must write every key or a stale one
 * survives, so all five are pushed unconditionally. Order within a transition
 * is immaterial — the keys are distinct and GTM's model is last-value-wins.
 *
 * Signed-out shape: every identity key is pushed with an EMPTY value
 * (`""` / `false`) rather than left in place. GTM's data model is
 * last-value-wins, so without an explicit clear the previous customer keeps
 * being reported on every event after logout (COM-417 item 2). Empty strings
 * are the pending-martech default — a GTM `reset` is the alternative; swap
 * here if martech asks for it.
 */
export const buildIdentityPushes = (
  id: DataLayerIdentity,
): Record<string, unknown>[] => {
  const pushes: Record<string, unknown>[] = id.isAuthenticated
    ? [
        { userId: id.userId ?? "" },
        { hashedEmail: id.hashedEmail ?? "" },
        { heapId: id.heapId ?? "" },
        { customerLoyalty: id.customerLoyalty },
      ]
    : [
        { userId: "" },
        { hashedEmail: "" },
        { heapId: "" },
        { customerLoyalty: false },
      ];
  pushes.push({ customerGroupSalesforce: JSON.stringify(id.customerGroups) });
  return pushes;
};

/**
 * Re-pushes the signed-in identity (`userId` / `hashedEmail` / `heapId` /
 * `customerLoyalty` / `customerGroupSalesforce`) when it changes WITHOUT a
 * hard load, and clears it on logout (COM-417 item 2).
 *
 * The first-paint push is `DataLayerClientPushes` — a run-once (`[]`-deps)
 * effect in the root shell that never re-runs on client navigation. The
 * account-drawer login is a fetcher submit (the root loader revalidates in
 * place) and logout is an SPA navigation to `/logout` (root `shouldRevalidate`
 * refreshes across auth paths), so neither re-mounts the shell: SFRA, being
 * MPA, gets the fresh identity for free on the next full render, Workers did
 * not — after logout the previous customer kept being reported.
 *
 * Mirrors `ProductDetailTracker` (SFCC-11804): seeded with the first-paint
 * signature so hydration doesn't double-push what `DataLayerClientPushes`
 * already sent, then pushes once per identity change.
 */
export function IdentityTracker(props: DataLayerIdentity) {
  const signature = identitySignature(props);
  const lastPushed = useRef<string>(signature);
  useEffect(() => {
    if (lastPushed.current === signature) return;
    lastPushed.current = signature;
    for (const entry of buildIdentityPushes(props)) pushDataLayer(entry);
    // `signature` is derived from every prop the pushes read, so keying on it
    // alone avoids a per-render effect run without going stale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);
  return null;
}
